import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BriefcaseBusiness, AlertCircle, Mail, Phone, GraduationCap } from "lucide-react";
import {
  AppButton, AppCard, AppCardContent, Callout, EmptyState, PageHeader, UserAvatar,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import StudentPortfolioSections from "./StudentPortfolioSections";
import IdentityCardSection from "../IdentityCard/IdentityCardSection";
import { fetchStudentPortfolio, errorText } from "./portfolioApi";

/**
 * StudentPortfolioView — a student's portfolio as its own page, for instructors.
 *
 * Admins read the same sections inside the student record (`/students/:id`,
 * Portfolio tab); their old `/students/:id/portfolio` link redirects there.
 *
 * The instructor case is the one that carries a rule: the server
 * (utils/portfolioAccess) allows an instructor through only for students in a
 * batch they are assigned to, and answers 403 otherwise. This screen shows that
 * refusal as a plain sentence rather than an error, because it is a normal
 * outcome — an instructor opening a link to a student they do not teach has
 * done nothing wrong.
 */

const StudentPortfolioView = ({ backPath = "/students" }) => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setDenied("");
    fetchStudentPortfolio(id)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        // 403 is the instructor-scope refusal; 404 is a student outside this
        // institute. Both are expected, neither is a fault to report as one.
        if (status === 403 || status === 404) {
          setDenied(errorText(err, "You do not have access to this student's portfolio."));
        } else {
          setError(errorText(err, "Failed to load the portfolio."));
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <Shimmer height={110} />
        <Shimmer height={260} />
        <Shimmer height={200} />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <PageHeader title="Student Portfolio" icon={BriefcaseBusiness} backButton />
        <EmptyState
          icon={AlertCircle}
          title="Not available to you"
          description={denied}
          action={<AppButton variant="outline" onClick={() => navigate(backPath)}>Go back</AppButton>}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-flex tw-flex-col tw-gap-4">
        <PageHeader title="Student Portfolio" icon={BriefcaseBusiness} backButton />
        <Callout tone="danger" role="alert">{error}</Callout>
      </div>
    );
  }

  const { student } = data;

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      {/* No `onBack` — PageHeader falls back to browser history, so this
          returns to whichever screen linked here rather than always the same one. */}
      <PageHeader
        title="Student Portfolio"
        description={student?.name}
        icon={BriefcaseBusiness}
        backButton
      />

      <AppCard>
        <AppCardContent className="tw-flex tw-flex-wrap tw-items-center tw-gap-4">
          <UserAvatar name={student?.name} size="lg" bordered />
          <div className="tw-min-w-0 tw-flex-1">
            <h2 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content">{student?.name}</h2>
            <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-content-muted">
              {student?.email && (
                <span className="tw-inline-flex tw-items-center tw-gap-1"><Mail size={12} /> {student.email}</span>
              )}
              {student?.mobile && (
                <span className="tw-inline-flex tw-items-center tw-gap-1"><Phone size={12} /> {student.mobile}</span>
              )}
              {student?.degree && (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                  <GraduationCap size={12} /> {student.degree}{student.passout ? ` · ${student.passout}` : ""}
                </span>
              )}
            </div>
          </div>
        </AppCardContent>
      </AppCard>

      {/* The student's ID card, for an institute printing one on their behalf.
          Outside the portfolio check on purpose: a card exists as soon as the
          student has a login, whether or not they have written a portfolio. */}
      <IdentityCardSection studentId={id} title="Student ID Card" />

      <StudentPortfolioSections studentId={id} data={data} />
    </div>
  );
};

export default StudentPortfolioView;
